<template>
  <section :class="tabsClasses">
    <ul class="cordel-navigation-tabs-items">
      <li
        v-for="(tab, index) in tabs"
        :key="tab.slot"
        :class="classes[index]"
      >
        <a
          :href="`#tab-${tab.slot}`"
          class="cordel-navigation-tabs-link"
          @click.prevent="select(tab)"
        >
          {{ tab.label }}
        </a>
      </li>

      <li class="cordel-navigation-tabs-more">
        <a
          :href="`#tabs`"
          class="cordel-navigation-tabs-more-toggler"
          @click.prevent="toggle"
        >
          Ver mais
          <font-awesome-icon
            class="cordel-navigation-tabs-more-toggler-icon"
            icon="chevron-down"
          />
        </a>
      </li>
    </ul>

    <ul class="cordel-navigation-tabs-subitems">
      <li
        v-for="(tab, index) in tabs"
        :key="tab.slot"
        :class="subclasses[index]"
      >
        <a
          :href="`#tab-${tab.slot}`"
          class="cordel-navigation-tabs-sublink"
          @click.prevent="subselect(tab)"
        >
          {{ tab.label }}
        </a>
      </li>
    </ul>

    <slot />
    <slot :name="active" />
  </section>
</template>

<script>
  export default {
    name: 'CordelNavigationTabs',
    props: {
      tabs: {
        type: Array,
        default() {
          return [];
        },
      },
    },
    data() {
      return {
        more: false,
        selected: null,
      };
    },
    computed: {
      active() {
        if (this.selected) {
          if (this.tabs.find(tab => tab.slot === this.selected)) {
            return this.selected;
          }
        }

        if (this.tabs[0]) {
          return this.tabs[0].slot;
        }

        return null;
      },
      classes() {
        return this.tabs.map((tab) => {
          const classes = ['cordel-navigation-tabs-item'];

          if (tab.slot === this.active) {
            classes.push('state--active');
          }

          return classes;
        });
      },
      subclasses() {
        return this.classes.map(i => i.map(j => j.replace(/-item$/, '-subitem')));
      },
      tabsClasses() {
        const classes = ['cordel-navigation-tabs'];

        if (this.tabs.length > 2) {
          classes.push('variant--more');
        }

        if (this.more) {
          classes.push('state--open');
        }

        return classes;
      },
    },
    mounted() {
      const initial = this.tabs.find(tab => tab.initial);

      if (initial) {
        this.select(initial);
      }
    },
    methods: {
      select(tab) {
        this.selected = tab.slot;

        this.$emit('selected', tab);
      },
      subselect(tab) {
        this.select(tab);

        this.toggle();
      },
      toggle() {
        this.more = !this.more;
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@cordel/assets/stylesheets/mixins/reset.scss';
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .cordel-navigation-tabs {
  }

  .cordel-navigation-tabs-items {
    align-items: flex-end;
    color: var(--cordel-legacy-color-neutral-primary);
    flex-direction: row;
    font-size: var(--cordel-legacy-font-size-xl);
    font-weight: var(--cordel-legacy-font-weight-bold);
    display: flex;
    justify-content: center;
    list-style: none;
    text-align: center;

    @media all and (max-width: $cordel-breakpoint-mobile) {
      font-size: var(--cordel-legacy-font-size-m);
    }
  }

  .cordel-navigation-tabs-item {
    flex: 1;

    @media all and (max-width: $cordel-breakpoint-mobile) {
      .variant--more &:not(.state--active) {
        display: none;
      }
    }
  }

  @mixin default-state {
    border-color: transparent;
    border-radius: var(--cordel-legacy-border-radius-small) var(--cordel-legacy-border-radius-small) 0 0;
    border-width: var(--cordel-legacy-border-width-heavy) 0;
    border-style: solid;
    border-bottom-color: var(--cordel-legacy-color-neutral-primary);
    display: block;
    padding: var(--cordel-legacy-spacing-xs);
    text-decoration: none;
    transition: background-color .1s,
                border-bottom-color .1s,
                color .1s;

    .cordel-navigation-tabs-item:first-child & {
      border-top-left-radius: 0;
    }

    .cordel-navigation-tabs-item:last-child & {
      border-top-right-radius: 0;
    }

    &:hover {
      background-color: var(--cordel-legacy-color-neutral-light);
      color: var(--cordel-legacy-color-constant-dark);
    }

    &:focus {
      border-width: var(--cordel-legacy-border-width-heavy);
      border-color: var(--cordel-legacy-color-highlight-primary);
      outline: none;
    }

    @media all and (max-width: $cordel-breakpoint-mobile) {
      padding: var(--cordel-legacy-spacing-xs) var(--cordel-legacy-spacing-xxs);
    }
  }

  .cordel-navigation-tabs-link {
    @include default-state;

    .state--active & {
      background-color: var(--cordel-legacy-color-neutral-lighter);
      border-bottom-color: var(--cordel-legacy-color-constant-dark);
      color: var(--cordel-legacy-color-constant-dark);
    }
  }

  .cordel-navigation-tabs-more {
    flex: 0;
    display: none;
    margin-left: var(--cordel-legacy-spacing-mini);
    white-space: nowrap;

    @media all and (max-width: $cordel-breakpoint-mobile) {
      .variant--more & {
        display: block;
      }
    }
  }

  .cordel-navigation-tabs-more-toggler {
    @include default-state;

    .state--open & {
      background-color: var(--cordel-legacy-color-neutral-lighter);
    }

    .state--open &:not(:focus) {
      border-bottom-color: transparent;
    }
  }

  .cordel-navigation-tabs-more-toggler-icon {
    margin-left: 0.25rem;
  }

  .cordel-navigation-tabs-subitems {
    background-color: var(--cordel-legacy-color-neutral-lighter);
    border-bottom: var(--cordel-legacy-border-width-heavy) solid var(--cordel-legacy-color-neutral-primary);
    display: none;
    list-style: none;
    padding: var(--cordel-legacy-spacing-xxxs);
    text-align: right;

    @media all and (max-width: $cordel-breakpoint-mobile) {
      .state--open & {
        display: block;
      }
    }
  }

  .cordel-navigation-tabs-subitem {
    &.state--active {
      display: none;
    }
  }

  .cordel-navigation-tabs-sublink {
    border-radius: var(--cordel-legacy-border-radius-small);
    color: var(--cordel-legacy-color-neutral-dark);
    display: block;
    font-weight: var(--cordel-legacy-font-weight-bold);
    padding: var(--cordel-legacy-spacing-nano);

    &:focus {
      box-shadow: 0 0 0 var(--cordel-legacy-border-width-thin) var(--cordel-legacy-color-highlight-primary);
      outline: none;
    }
  }
</style>
